#!/usr/bin/env node

// Importing necessary modules from NPM packages
import { Command } from "commander";
import fs from "fs-extra";
import path from "path";
import { fileURLToPath } from "url";
import ora from "ora";
import chalk from "chalk";

// Initializing the command instance for syncing files
export const sync = new Command();

sync
  .name("sync")
  .description(
    chalk.bgBlueBright(chalk.black("Sync design system files to package")),
  )
  .action(async () => {
    const __filename = fileURLToPath(import.meta.url);
    const __dirname = path.dirname(__filename);
    const DESIGN_SYSTEM_DIR = path.join(__dirname, "../../../apps/web");
    const SYSTEM_DIR = path.join(__dirname, "../system");
    const TO_SYNC = [
      "design-hooks",
      "assets",
      "tailwind.config.ts",
      "components",
      "design-lib",
      "core",
      "design-types",
    ];
    const textualExtensions = [".ts", ".tsx", ".js", ".css", ".html", ".json"];

    const globalSpinner = ora({
      text: chalk.hex("#FFA500").bold("Initializing Sync 🛠️"),
      spinner: "dots",
      color: "yellow",
    }).start();

    /**
     * Syncs files and directories from source to destination
     * @param {string} source - The source path
     * @param {string} destination - The destination path
     */
    const syncFiles = async (source: string, destination: string) => {
      const syncSpinner = ora({
        text: chalk.hex("#4CAF50").bold(`Syncing ${source}... 🔄`),
        spinner: "star2",
        color: "green",
      }).start();

      try {
        const exists = await fs.pathExists(source);
        if (!exists) {
          throw new Error(`Source path "${source}" doesn't exist.`);
        }

        const stats = await fs.stat(source);

        if (stats.isFile()) {
          let destPath = destination;
          const ext = path.extname(destination);
          if (textualExtensions.includes(ext)) {
            destPath = `${destination}.txt`;
          }
          await fs.copy(source, destPath);
        } else if (stats.isDirectory()) {
          await fs.ensureDir(destination);
          const files = await fs.readdir(source);
          for (const file of files) {
            await syncFiles(
              path.join(source, file),
              path.join(destination, file),
            );
          }
        }

        // Stop individual spinner for this sync operation
        syncSpinner.succeed(chalk.green(`${path.basename(source)}`));
      } catch (syncError) {
        syncSpinner.fail(
          chalk.red(`Failed to sync ${path.basename(source)} ❌`),
        );
        console.error(
          chalk.bgRedBright(
            chalk.black(" An error occurred during file syncing: "),
          ) + chalk.red(syncError),
        );
      }
    };

    try {
      globalSpinner.text = chalk
        .hex("#FFA500")
        .bold("Starting Sync Process 🚀");

      for (const item of TO_SYNC) {
        const srcPath = path.join(DESIGN_SYSTEM_DIR, item);
        const destPath = path.join(SYSTEM_DIR, item);
        await syncFiles(srcPath, destPath);
      }

      // Stop global spinner after all syncing is complete
      globalSpinner.succeed(chalk.greenBright.bgBlack("🎉 Sync complete 🎉"));
    } catch (error) {
      globalSpinner.fail(chalk.red("Sync process failed 🛑"));
      console.error(
        chalk.bgRedBright(chalk.black(" An error occurred during syncing: ")) +
          chalk.red(error),
      );
    }
  });
